import { Flex, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

type StatusIconProps = {
  background: string;
  backgroundDark: string;
  icon: string;
  label: string;
  textColor: string;
  textColorDark: string;
};

const statusStyles: {
  [key in DataPipeline.Status]: StatusIconProps;
} = {
  success: {
    background: colors.green[100],
    backgroundDark: 'rgba(72, 187, 120, 0.16)',
    icon: 'check-circle',
    label: 'Complete',
    textColor: colors.green[400],
    textColorDark: colors.green[300],
  },
  error: {
    background: colors.red[100],
    backgroundDark: 'rgba(245, 101, 101, 0.16)',
    icon: 'circle-xmark',
    label: 'Error',
    textColor: colors.red[400],
    textColorDark: colors.red[300],
  },
  inProgress: {
    background: colors.blue[100],
    backgroundDark: 'rgba(66, 153, 225, 0.16)',
    icon: 'spinner',
    label: 'In Progress',
    textColor: colors.blue[400],
    textColorDark: colors.blue[300],
  },
  waitingForAssignment: {
    background: colors.orange[100],
    backgroundDark: 'rgba(237, 137, 54, 0.16)',
    icon: 'circle-exclamation',
    label: 'Assignment Required',
    textColor: colors.orange[400],
    textColorDark: colors.orange[300],
  },
  waitingForInput: {
    background: colors.orange[100],
    backgroundDark: 'rgba(237, 137, 54, 0.16)',
    icon: 'circle-exclamation',
    label: 'Inputs Required',
    textColor: colors.orange[400],
    textColorDark: colors.orange[300],
  },
  waitingForVerificationApproval: {
    background: colors.orange[100],
    backgroundDark: 'rgba(237, 137, 54, 0.16)',
    icon: 'circle-exclamation',
    label: 'Verification Required',
    textColor: colors.orange[400],
    textColorDark: colors.orange[300],
  },
  waitingForReconcileApproval: {
    background: colors.orange[100],
    backgroundDark: 'rgba(237, 137, 54, 0.16)',
    icon: 'circle-exclamation',
    label: 'Review Required',
    textColor: colors.orange[400],
    textColorDark: colors.orange[300],
  },
  expired: {
    background: colors.gray[100],
    backgroundDark: 'rgba(160, 174, 192, 0.16)',
    icon: 'calendar-circle-exclamation',
    label: 'Expired',
    textColor: colors.gray[400],
    textColorDark: colors.gray[300],
  },
};

export const PipelineStatusIndicator = ({
  status,
  // When true, render only the colored status icon (no text label). Used by
  // compact list views that surface the full label on row expansion instead.
  iconOnly = false,
}: {
  status: DataPipeline.Status;
  iconOnly?: boolean;
}) => {
  const styles = statusStyles[status];
  finleyAssert(styles, `Unknown pipeline status:${status}`);

  const background = useColorModeValue(
    styles.background,
    styles.backgroundDark,
  );
  const textColor = useColorModeValue(styles.textColor, styles.textColorDark);

  return (
    <Flex
      as="span"
      height="100%"
      padding={iconOnly ? '7px' : '7px 12px'}
      background={background}
      borderRadius="7px"
      display="inline-flex"
      alignItems="center"
      gap={2}
      color="white"
      whiteSpace="nowrap"
      aria-label={iconOnly ? styles.label : undefined}
    >
      <FaIcon icon={styles.icon} color={textColor} />
      {!iconOnly && (
        <Text as="span" color={textColor} size="14px">
          {styles.label}
        </Text>
      )}
    </Flex>
  );
};

export const getStatusIcon = (status: DataPipeline.Status): StatusIconProps => {
  const styles = statusStyles[status];
  return styles;
};

export const getStatusText = (status: DataPipeline.Status): StatusIconProps => {
  const styles = statusStyles[status];
  return styles;
};

export const TestRender = () => {
  return (
    <VStack>
      {DataPipeline.StatusIterables.map((status) => {
        return <PipelineStatusIndicator key={status} status={status} />;
      })}
    </VStack>
  );
};
